import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '~/lib/api';
import { Tone, WaitingInline } from '~/components/ui';
import PaymentPausedNotice from './components/PaymentPausedNotice';
import { useT } from '~/i18n';
import PlanOffer from './components/PlanOffer';
import { openBillingPortal } from './api/billingPortalClient';
import {
  NO_PLAN,
  PLAN_PRICE,
  formatEndedPlanDate,
  formatPlanDate,
  planLabel,
  planRenewalPrice,
  readPlan,
  type PlanState,
} from './lib/plan';
import { usePlanCta } from './lib/planCta';

/**
 * La tab Plan del perfil: qué compró, hasta cuándo vale y cómo se renueva.
 *
 * Vive en Perfil y no en el panel porque el panel es el trabajo del día —
 * postulaciones y respuestas— y el plan es una de esas cosas que se consultan
 * de vez en cuando. El muro de pago sí aparece en el panel, en el momento
 * en que se descubre; acá está la versión tranquila, la que se viene a mirar.
 *
 * `plan_active` en mint, y SÓLO cuando está activo: es un hecho comprobado
 * contra Stripe, no una promesa. Un plan vencido es fricción y va en coral.
 */
export default function JobApplyPlanPage() {
  // `useT` también re-renderiza al cambiar el idioma, y con eso las fechas
  // (`lib/date` lee el idioma activo al formatear).
  const t = useT('plan');
  const [plan, setPlan] = useState<PlanState>(NO_PLAN);
  const { case: ctaCase } = usePlanCta(plan);
  // Con free_tier el servidor dice que quien entra ya tiene TBit Jobs sin plan.
  const [freeTier, setFreeTier] = useState(false);
  const [loading, setLoading] = useState(true);
  // Un booleano y no el texto: el mensaje se lee del diccionario al pintar.
  const [err, setErr] = useState(false);
  // El portal de Stripe: abriéndose, o por qué no se pudo (clave del diccionario).
  const [opening, setOpening] = useState(false);
  const [manageErr, setManageErr] = useState<'noBillingAccount' | 'failed' | null>(null);

  const handleManage = async () => {
    setManageErr(null);
    setOpening(true);
    const result = await openBillingPortal();
    if (result.status === 'ok') {
      // Sin apagar `opening`: la página se va, y el botón sigue diciendo que se está abriendo.
      window.location.assign(result.url);
      return;
    }
    setManageErr(result.status === 'no_billing_account' ? 'noBillingAccount' : 'failed');
    setOpening(false);
  };

  // Se desmontó: las respuestas que lleguen tarde no se pintan.
  const alive = useRef(true);

  // onboarding-status ya trae los campos del plan y es la respuesta más barata
  // de las dos: el dashboard cuenta postulaciones que acá no se pintan. Se vuelve
  // a llamar (sin cargador) cuando el checkout dice `already_subscribed`.
  const loadPlan = useCallback(() => {
    apiFetch('/job-apply/api/onboarding-status')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!alive.current || !data) return;
        setPlan(readPlan(data));
        setFreeTier((data as { free_tier?: unknown }).free_tier === true);
      })
      .catch(() => {
        if (alive.current) setErr(true);
      })
      .finally(() => {
        if (alive.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    alive.current = true;
    loadPlan();
    return () => { alive.current = false; };
  }, [loadPlan]);

  if (loading) return <WaitingInline label={t.page.loading} />;

  const renewsOn = formatPlanDate(plan.renews_at);
  const renewalPrice = planRenewalPrice(plan.plan);
  // En la prueba, la fecha que importa es la del primer cobro.
  const trialEndsOn = plan.trialing ? formatPlanDate(plan.trial_ends_at ?? plan.renews_at) : '';
  // Sólo se nombra la fecha del final cuando ya pasó — ver `formatEndedPlanDate`.
  const endedOn = formatEndedPlanDate(plan.renews_at);

  // Administrar o cancelar en el portal de Stripe. Siempre que haya cuenta de
  // cobro: con el plan vencido también, porque ahí es donde se cambia la tarjeta
  // y se recupera un cobro fallido.
  const manage = (lead?: string) => (
    <div className="space-y-2">
      {lead && <p className="text-sm">{lead}</p>}
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={handleManage}
        disabled={opening}
      >
        {opening ? t.page.manage.opening : t.page.manage.cta}
      </button>
      {!lead && <p className="text-xs hush">{t.page.manage.note}</p>}
      {manageErr && (
        <div role="alert" className="alert alert-error text-sm">
          {t.page.manage[manageErr]}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {err && <div className="alert alert-error text-sm">{t.page.loadError}</div>}

      <PaymentPausedNotice plan={plan} />

      {plan.plan_active ? (
        <div className="card">
          <div className="card-body space-y-3">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <span className="brand-label block mb-1">{t.yourPlan}</span>
                <h2 className="card-title">{planLabel(plan.plan)}</h2>
              </div>
              {plan.sending_paused ? (
                <Tone tone="warn">{t.paused.title}</Tone>
              ) : (
                <Tone tone="good">{plan.trialing ? t.page.trialing : t.page.active}</Tone>
              )}
            </div>

            {plan.trialing ? (
              trialEndsOn && (
                <p className="text-sm">
                  {t.page.trialBefore}
                  <span className="font-semibold">{trialEndsOn}</span>
                  {t.page.trialAfter(PLAN_PRICE)}
                </p>
              )
            ) : (
              renewsOn && (
                <p className="text-sm">
                  {t.page.renewsBefore}
                  <span className="font-semibold">{renewsOn}</span>
                  {renewalPrice ? t.page.renewsAfter(renewalPrice) : t.page.renewsPlain}
                </p>
              )
            )}

            <p className="text-sm hush">
              {t.page.activeBody}
            </p>

            {plan.can_manage_billing ? (
              manage()
            ) : (
              <p className="text-xs hush">
                {t.page.changeOrCancel}
              </p>
            )}
          </div>
        </div>
      ) : plan.plan ? (
        <>
          <div
            className="card"
            style={{ borderColor: 'rgba(255,130,112,0.4)', background: 'rgba(255,130,112,0.09)' }}
          >
            <div className="card-body py-4 flex-row items-center justify-between gap-4 flex-wrap">
              <p className="text-sm">
                <strong>{planLabel(plan.plan)}</strong>
                {endedOn ? t.page.endedOn(endedOn) : t.page.notCurrent}
                {t.page.meanwhile}
              </p>
              <Tone tone="bad">{t.page.expired}</Tone>
            </div>
          </div>

          {/* ANTES de la oferta: si fue un cobro fallido, cambiar la tarjeta
              recupera la misma suscripción sin pasar por un checkout nuevo. */}
          {plan.can_manage_billing && (
            <div className="card">
              <div className="card-body py-4">{manage(t.page.manage.failedCharge)}</div>
            </div>
          )}

          <PlanOffer
            headline={freeTier ? t.page.compare.headline : t.page.reactivate.headline}
            variant={freeTier ? 'compare' : undefined}
            lede={t.page.reactivate.lede}
            ctaCase={ctaCase}
            surface="plan_page"
            partnerDiscount={plan.partner_discount}
            onAlreadySubscribed={loadPlan}
          />
        </>
      ) : (
        <>
          {/* Sin plan reconocido pero con cuenta de cobro (p. ej. una suscripción
              que el resumen no nombra): el portal igual está a mano. */}
          {plan.can_manage_billing && (
            <div className="card">
              <div className="card-body py-4">{manage()}</div>
            </div>
          )}
          <PlanOffer
            headline={freeTier ? t.page.compare.headline : t.page.discover.headline}
            variant={freeTier ? 'compare' : undefined}
            lede={t.page.discover.lede}
            ctaCase={ctaCase}
            surface="plan_page"
            partnerDiscount={plan.partner_discount}
            onAlreadySubscribed={loadPlan}
          />
        </>
      )}
    </div>
  );
}
